import { Delete } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import { useMemo, useState } from "react";
import { tableConfig } from "../../../commons/table/tableConfig";
import { StyledResolucion } from "../../../styled/Prevencion";
import FinalizarFormularioButton from "../FinalizarFormularioButton";

const TrabajadoresSectorTable = ({ data, handleOpenFormularioTrabajador, handleDelete, handleCancelResolucion }) => {
  const [rowSelection, setRowSelection] = useState({});
  const maxInicioExposicion = moment().subtract(1, 'year').endOf('year');

  const hasInicioExposicionPosteriorLimite = (worker) => {
    const fechasInicio = [
      worker?.inicioContacto,
      ...Object.values(worker?.examenes || {}).map((sub) => sub?.inicioContacto),
    ].filter(Boolean);

    return fechasInicio.some((fecha) => {
      const m = moment(fecha);
      return m.isValid() && m.isAfter(maxInicioExposicion, 'day');
    });
  };

  const isWorkerIncomplete = (worker) => {
    if (!worker.idSector || !worker.idPuesto) return true;

    if (hasInicioExposicionPosteriorLimite(worker)) return true;

    // Si no está exento de exposición (exposicion === false significa QUE SI ESTA EXPUESTO en este contexto)
    if (!worker.sinExposicion) {
      const hasExcelSubstance = !!worker.idSustanciaInterna;
      const hasManualSubstances = worker.examenes && Object.keys(worker.examenes).length > 0;

      // Si el trabajador tiene sustancias asociadas (de excel o manual), validar las fechas
      if (hasExcelSubstance || hasManualSubstances) {
        // Validar fechas por sustancia
        if (worker.examenes && Object.keys(worker.examenes).length > 0) {
          return Object.values(worker.examenes).some(
            (sub) => !sub.inicioContacto
          );
        }

        // Si venía de excel con una sola sustancia (caso legacy o simple) y no tiene el objeto examenes poblado aún
        if (hasExcelSubstance && !worker.inicioContacto) {
          return true;
        }
      }
      // Si no tiene sustancias asociadas, está bien (el puesto puede no tener sustancias)
    }
    return false;
  };

  const columns = useMemo(() => [
    {
      accessorKey: "cuil",
      header: "CUIL",
      filterVariant: 'text',
      size: 120,
    },
    {
      accessorKey: "nombreApellidoTrabajador",
      header: "Apellido y Nombre",
      filterVariant: 'text',
      size: 200,
    },
    {
      accessorKey: "nombreSector",
      header: "Sector",
      filterVariant: 'text',
      size: 150,
    },
    {
      accessorKey: "nombrePuesto",
      header: "Puesto",
      filterVariant: 'text',
      size: 150,
    },
    {
      accessorKey: "fechaIngreso",
      header: "F. Ingreso",
      filterVariant: 'text',
      size: 120,
      Cell: ({ cell }) => cell.getValue() ? moment(cell.getValue()).format("DD/MM/YYYY") : "",
    },
    {
      id: "exposicion",
      header: "Exposición R81",
      size: 120,
      Cell: ({ row }) => row.original.examenes && Object.keys(row.original.examenes).length > 0 ? "SI" : "NO",
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: data || [],
    enableRowSelection: true,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowActions: true,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Operaciones',
        size: 120,
      },
    },
    renderRowActions: ({ row }) => (
      <Tooltip title="Editar Trabajador">
        <IconButton
          onClick={() => handleOpenFormularioTrabajador({ ...row.original, index: row.index })}
          color="primary"
          size="small"
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
    ),
    renderTopToolbarCustomActions: () => (
      <StyledResolucion.HeaderContainer>
        <StyledResolucion.TableTitle>
          Trabajadores Para Informar en la Muestra
        </StyledResolucion.TableTitle>
      </StyledResolucion.HeaderContainer>
    ),
    renderBottomToolbarCustomActions: () => (
      <StyledResolucion.TableFooterButtonContainer>
        <Button
          color="primary"
          onClick={() => handleOpenFormularioTrabajador()}
        >
          Agregar
        </Button>
      </StyledResolucion.TableFooterButtonContainer>
    ),
    muiPaginationProps: {
      labelRowsPerPage: "Filas por página:",
      getItemAriaLabel: (type) => {
        if (type === 'first') return 'ir a la primera página';
        if (type === 'last') return 'ir a la última página';
        if (type === 'next') return 'ir a la página siguiente';
        return 'ir a la página anterior';
      },
      labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
    },
    localization: {
      ...MRT_Localization_ES,
      noRecordsToDisplay: "No hay datos que mostrar",
      MRT_RowsSelected: '{0} Fila(s) seleccionadas',
    },
    initialState: {
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      density: 'compact',
    },
    enableColumnFilters: true,
    enableFilters: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: true,
    paginationDisplayMode: 'pages',
    muiTableHeadCellProps: {
      sx: { ...tableConfig.muiTableHeadCellProps?.sx }
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        ...tableConfig.muiTableBodyRowProps?.sx,
        backgroundColor: isWorkerIncomplete(row.original) ? '#ffebee' : 'inherit', // Light red for incomplete
      },
      title: isWorkerIncomplete(row.original) ? 'Trabajador incompleto: Faltan datos obligatorios (Sector, Puesto o Exposición)' : '',
    }),
    muiFilterTextFieldProps: {
      placeholder: 'Filtrar...',
      size: 'small',
      variant: 'outlined',
    },
    renderToolbarAlertBannerContent: ({ groupedAlert, selectedAlert, table }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginLeft: 1,
        }}>
        {selectedAlert}
        <Tooltip title="Eliminar seleccionados">
          <IconButton
            onClick={() => {
              const selectedRows = table.getSelectedRowModel().rows.map(row => row.index)
              handleDelete(selectedRows);
              table.resetRowSelection();
            }}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  return (
    <>
      <StyledResolucion.TableContainer>
        <MaterialReactTable table={table} />
      </StyledResolucion.TableContainer>
      <StyledResolucion.ButtonContainer>
        <StyledResolucion.CancelButton
          variant="contained"
          onClick={handleCancelResolucion}
        >
          CANCELAR FORMULARIO
        </StyledResolucion.CancelButton>
        <FinalizarFormularioButton />
      </StyledResolucion.ButtonContainer>
    </>
  );
};

export default TrabajadoresSectorTable;